import {
    AuthContext,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    FaxDetail,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderSendFaxesRequest,
    ProviderFaxSortType,
    ProviderSendFax,
} from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import * as S from './outbox.styles';

interface IFaxProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IFaxState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<ProviderSendFax>[] = [
    {
        id: 'External ID',
        Header: 'ExternalId',
        sortType: ProviderFaxSortType.EXTERNALID,
        accessor: 'externalId',
        Cell: DataTableCell('String'),
        show: false,
    },
    {
        id: 'FromE164',
        Header: 'Sender phone',
        accessor: 'fromE164',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'ToE164',
        Header: 'Receiver phone',
        accessor: 'toE164',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'Retreived',
        Header: 'Retreived fax',
        accessor: 'retriedFaxId',
        Cell: DataTableCell('Boolean'),
        disableSortBy: true,
        show: false,
    },
    {
        id: 'Pages',
        Header: '# of Pages',
        accessor: 'pages',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'Sent',
        Header: 'Sent date',
        accessor: 'sent',
        Cell: DataTableCell('DateTime'),
        disableSortBy: true,
    },
    {
        id: 'CreatedDate',
        sortType: ProviderFaxSortType.CREATED,
        defaultSort: { id: 'CreatedDate', desc: true },
        Header: 'Created Date',
        accessor: 'created',
        Cell: DataTableCell('DateTime'),
        show: false,
    },
];
const filterSchemas: IFilterSchema<keyof ListProviderSendFaxesRequest>[] = [
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    },
    {
        label: 'Document Id',
        filterId: 'filterDocumentId',
        columnId: 'documentId',
        filterType: 'String',
    },
    {
        label: 'Created Date',
        filterId: 'filterCreated',
        columnId: 'created',
        filterType: 'Date',
    },
];

class FaxPage extends React.Component<IFaxProps, IFaxState> {
    private presenter: DataTablePresenter<ProviderSendFax>;

    constructor(props: IFaxProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderSendFax>({
            name: 'fax-send',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Sent Faxes';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('fax-send');
    }

    public render(): React.ReactElement {
        return (
            <DataTable
                presenter={this.presenter}
                isLoading={this.state.loading}
                onFetch={this.onFetch}
                onRowClick={this.onRowClick}
                renderRowSubComponent={this.renderRowSubComponent}
            />
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        if (!_.isEqual(this.state.options, fetchOptions)) {
            try {
                this.setState({
                    options: fetchOptions,
                    loading: true,
                });
                this.authContext.cancelGroup('fax-send');
                this.authContext.setGroup('fax-send');

                const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
                const sortAndOrder = parseSortBy(sortBy, columns);
                const faxListing = await this.props.rootStore.claimStore.loadSendFaxListing({
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                });
                this.presenter.onDataUpdate(faxListing.items || []);
                this.presenter.onDataTotalChange(faxListing.total || 0);
                this.setState({
                    loading: false,
                });
            } catch (err) {
                this.dialogContext.showError(err);
            }
        }
    };

    private onRowClick = async (row: Row<ProviderSendFax>): Promise<void> => {
        // retrieve mail cart if necessary
        row.toggleRowExpanded();
    };

    private renderRowSubComponent = (row: Row<ProviderSendFax>): React.ReactElement => {
        return (
            <S.Expand>
                <FaxDetail 
                    fax={row.original}
                    appType="provider"
                    downloadDocument={this.props.rootStore.claimStore.viewFaxById.bind(this.props.rootStore.claimStore)}
                />
            </S.Expand>
        );
    };
}

export const OutboxFax: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            return <FaxPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
        },
    ),
);
